import { useCallback } from "react";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import { IconButton, Popover, Tooltip } from "@mui/material";
import {
  HISTORY_DROPDOWN_MAX_HEIGHT,
  HISTORY_DROPDOWN_WIDTH,
} from "../../AiToolboxSection.constants";
import type { ChatSession } from "../../AiToolboxSection.types";
import ChatHistory from "../ChatHistory/ChatHistory";
import { useMenuState } from "./ChatHistoryDropdown.hooks";

interface ChatHistoryDropdownProps {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSwitchSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => void;
}

const ChatHistoryDropdown = ({
  sessions,
  activeSessionId,
  onSwitchSession,
  onDeleteSession,
  onRenameSession,
}: ChatHistoryDropdownProps) => {
  const { anchorEl, isOpen, handleOpen, handleClose } = useMenuState();

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      onSwitchSession(sessionId);
      handleClose();
    },
    [onSwitchSession, handleClose]
  );

  const isEmpty = sessions.length === 0;

  return (
    <>
      <Tooltip title="Chat history">
        <IconButton
          size="small"
          onClick={handleOpen}
          className="text-text-secondary hover:text-text-primary"
          disabled={isEmpty}
          data-ph-capture-attribute-action="open_chat_history"
        >
          <HistoryRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: HISTORY_DROPDOWN_MAX_HEIGHT,
              width: HISTORY_DROPDOWN_WIDTH,
              overflowY: "auto",
            },
          },
        }}
      >
        <ChatHistory
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSwitchSession={handleSelectSession}
          onDeleteSession={onDeleteSession}
          onRenameSession={onRenameSession}
        />
      </Popover>
    </>
  );
};

export default ChatHistoryDropdown;
